/*
 * Shade swatches on the product card (snippets/card-swatches.liquid).
 *
 * Hovering a swatch previews that shade on the card image and in the shade
 * name line; clicking commits it, which also points the card's links at that
 * variant so the product page opens on the shade the customer picked.
 *
 * Listeners are delegated from the document rather than bound per card. The
 * grid is re-rendered wholesale by filtering, sorting, pagination and
 * predictive search, all of which replace card markup without firing a hook
 * this file could listen for - anything bound per element would go dead
 * after the first filter click.
 */

class CardSwatches {
  static CARD = '[data-product-card]';
  static SWATCH = '[data-card-swatch]';

  constructor() {
    document.addEventListener('pointerover', this.onPointerOver.bind(this));
    document.addEventListener('pointerout', this.onPointerOut.bind(this));
    document.addEventListener('click', this.onClick.bind(this));

    // Keyboard users get the same preview as pointer users: focus in previews,
    // focus out restores, and Enter/Space fire click, which commits.
    document.addEventListener('focusin', this.onPointerOver.bind(this));
    document.addEventListener('focusout', this.onPointerOut.bind(this));
  }

  onPointerOver(event) {
    const swatch = event.target.closest?.(CardSwatches.SWATCH);
    if (!swatch) return;

    const card = swatch.closest(CardSwatches.CARD);
    if (!card) return;

    this.preview(card, swatch);
  }

  onPointerOut(event) {
    const swatch = event.target.closest?.(CardSwatches.SWATCH);
    if (!swatch) return;

    const card = swatch.closest(CardSwatches.CARD);
    if (!card) return;

    // pointerout also fires when the pointer moves between a swatch's own
    // child elements. relatedTarget still being inside the same swatch means
    // the pointer never actually left it, so there is nothing to restore.
    if (event.relatedTarget?.closest?.(CardSwatches.SWATCH) === swatch) return;

    this.restore(card);
  }

  onClick(event) {
    const swatch = event.target.closest?.(CardSwatches.SWATCH);
    if (!swatch) return;

    const card = swatch.closest(CardSwatches.CARD);
    if (!card) return;

    // The card title's link is stretched across the whole card, so a click
    // anywhere lands on the product page. A swatch has to opt out of that.
    event.preventDefault();

    card.querySelectorAll(CardSwatches.SWATCH).forEach((element) => {
      element.classList.toggle('card__swatch--selected', element === swatch);
      element.querySelector('.swatch')?.classList.toggle('swatch--selected', element === swatch);
    });

    const variantUrl = swatch.dataset.variantUrl;
    if (variantUrl) {
      card.querySelectorAll('[data-card-link]').forEach((link) => {
        link.setAttribute('href', variantUrl);
      });
    }

    this.preview(card, swatch);
  }

  preview(card, swatch) {
    this.setImage(card, swatch.dataset.image, swatch.dataset.imageSrcset);
    this.setName(card, swatch.dataset.swatchValue);
  }

  /* Back to whichever shade is currently selected. That is the shade the
     customer clicked, or - before any click - the product's default, whose
     photo is already the card image, so this is a no-op on a first hover
     that ends without a selection. */
  restore(card) {
    const selected = card.querySelector('.card__swatch--selected');

    if (selected) {
      this.preview(card, selected);
      return;
    }

    const image = card.querySelector('[data-card-image]');
    if (image?.dataset.originalSrc) {
      image.setAttribute('src', image.dataset.originalSrc);
      image.setAttribute('srcset', image.dataset.originalSrcset || '');
      card.classList.remove('card--swatch-active');
    }

    this.setName(card, card.querySelector('[data-card-swatch-name]')?.dataset.originalName);
  }

  setImage(card, src, srcset) {
    const image = card.querySelector('[data-card-image]');
    if (!image || !src) return;

    // Captured on first use rather than at startup: on a filtered collection
    // page most cards are never touched, and reading src/srcset off every
    // image up front costs more than it saves.
    if (image.dataset.originalSrc === undefined) {
      image.dataset.originalSrc = image.getAttribute('src') || '';
      image.dataset.originalSrcset = image.getAttribute('srcset') || '';
    }

    image.setAttribute('src', src);
    image.setAttribute('srcset', srcset || src);

    // Suppresses the rollover image, which belongs to the product's default
    // shade and would otherwise contradict the shade being previewed.
    card.classList.add('card--swatch-active');
  }

  setName(card, value) {
    const label = card.querySelector('[data-card-swatch-name]');
    if (!label || !value) return;

    if (label.dataset.originalName === undefined) {
      label.dataset.originalName = label.textContent.trim();
    }

    label.textContent = value;
  }
}

new CardSwatches();